// /iris/irisfrontend/src/components/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import api from '../services/api';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, loading } = useAuth();

  /* ===========================================================
      Redirection si déjà connecté
     =========================================================== */
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  /* ===========================================================
      Récupération message après inscription
     =========================================================== */
  useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      setSuccess(state.message);

      if (state.email) {
        setFormData(prev => ({ ...prev, email: state.email }));
      }

      // Nettoyer l'historique
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  /* ===========================================================
      Gestion du formulaire
     =========================================================== */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login/', formData);
      const data = response.data;

      // Appliquer les tokens dans notre AuthProvider
      login(data.token, data.refresh_token, data.user);

      setSuccess('Connexion réussie !');

      setTimeout(() => {
        const redirect = localStorage.getItem('redirectAfterLogin') || '/';
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirect, { replace: true });
      }, 1000);

    } catch (err: any) {
      let message = 'Impossible de se connecter. Vérifiez vos identifiants.';

      if (err.response?.data?.error) message = err.response.data.error;
      if (err.response?.data?.message) message = err.response.data.message;

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ===========================================================
      UI
     =========================================================== */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md border border-gray-100"
      >
        {/* Logo / Branding */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto"
          >
            <span className="text-white text-3xl font-bold">IS</span>
          </motion.div>

          <h1 className="text-3xl font-semibold text-gray-800 mt-4">
            Connexion
          </h1>
          <p className="text-gray-500 mt-1">
            Accédez à votre espace IRIS SOFTECH
          </p>
        </div>

        {/* Alerts */}
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-green-50 text-green-700 border border-green-200 px-4 py-3 rounded-lg text-sm mb-4"
          >
            {success}
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4"
          >
            {error}
          </motion.div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm text-gray-700 font-medium">Email</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label className="text-sm text-gray-700 font-medium">Mot de passe</label>
            <input
              type="password"
              name="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Connexion...
              </span>
            ) : (
              'Se connecter'
            )}
          </button>
        </form>

        {/* Liens */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Pas encore de compte ?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-blue-600 font-medium hover:text-blue-800"
            >
              S'inscrire
            </button>
          </p>

          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 mt-3"
          >
            ← Retour à l'accueil
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
